import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Zap,
  ArrowUpRight,
  Check,
  Target,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Globe,
  Shield,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Zap, title: 'No-code builder', text: 'Create rules with triggers, conditions, and actions using a visual builder.' },
  { icon: Target, title: 'Smart ticket routing', text: 'Route tickets by channel, topic, sentiment, priority, or customer value.' },
  { icon: Clock, title: 'Auto-replies and follow-ups', text: 'Send instant acknowledgements, reminders, and updates on any channel.' },
  { icon: Sparkles, title: 'Tagging and scoring', text: 'Auto-tag leads, escalate VIPs, and score conversations for sales follow-up.' },
];

const benefits = [
  { icon: Zap, title: '70% manual work saved', description: 'Automate repetitive tasks and free up agents for complex issues.' },
  { icon: Clock, title: '5x faster routing', description: 'Tickets are routed to the right team instantly based on rules.' },
  { icon: Target, title: '-80% missed follow-ups', description: 'Automated reminders ensure no customer is forgotten.' },
  { icon: Globe, title: 'Omnichannel automation', description: 'Run the same logic across WhatsApp, email, chat, and tickets.' },
];

const useCases = [
  { title: 'Order status updates', text: 'Send automated WhatsApp or email updates when an order changes.' },
  { title: 'Lead qualification', text: 'Tag and score inbound leads based on channel, message, and intent.' },
  { title: 'SLA escalations', text: 'Escalate tickets automatically when first-response time is missed.' },
  { title: 'Customer onboarding', text: 'Automate welcome sequences and check-in messages for new users.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'Do I need to code workflows?',
    answer: 'No. ZuriHub workflows are built with a visual, no-code rule editor. Technical teams can also use webhooks for advanced integrations.',
  },
  {
    question: 'Which channels can workflows run on?',
    answer: 'Workflows work across WhatsApp, email, live chat, Instagram, Telegram, Messenger, X, and tickets.',
  },
  {
    question: 'Can workflows use AI?',
    answer: 'Yes. Zyra AI can detect intent, sentiment, and language to trigger the right workflow or route a conversation.',
  },
  {
    question: 'How do I monitor workflow performance?',
    answer: 'Every workflow has an activity log and error tracking. You can see which rules fire, how often, and troubleshoot issues in real time.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function WorkflowsAndAutomationsPage() {
  return (
    <>
      <SEO
        title="Workflows and Automations - No-Code Support Automation | ZuriHub"
        description="Automate support, sales, and operations with ZuriHub workflows. Route tickets, tag conversations, send follow-ups, and trigger actions across channels without code."
        path="/workflows-and-automations"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Zap className="h-4 w-4" /> Pro & Enterprise
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Put repetitive work on <span className="gradient-text">autopilot</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Automate support, sales, and operations with no-code workflows. Route tickets, tag conversations, schedule follow-ups, and trigger actions across channels.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Zap className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Automate.webp"
                  alt="ZuriHub Workflows and Automations showing no-code workflow builder"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful automation features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to automate support and sales.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why teams choose automation</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform support operations.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Choose a trigger, add conditions, set actions, and publish. Monitor performance in real time and scale as your volume grows.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Zap,
                    title: 'Choose a trigger',
                    description: 'Start with an event like a new ticket, a keyword, or a missed SLA.',
                  },
                  {
                    icon: Target,
                    title: 'Add conditions',
                    description: 'Filter by channel, customer, sentiment, priority, or custom fields.',
                  },
                  {
                    icon: Clock,
                    title: 'Set actions',
                    description: 'Route, tag, reply, notify, or create a task in another system.',
                  },
                  {
                    icon: Shield,
                    title: 'Publish and monitor',
                    description: 'Turn the workflow on and watch the activity log in real time.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Workflow automation features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> No-code visual builder</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Smart ticket routing</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Auto-replies and follow-ups</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> AI intent detection</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Omnichannel execution</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Audit logs and rollback</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Automate your first workflow</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Set up a no-code rule and start saving hours every week.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about workflows</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
